/**
 * Fallback dimensions for HTML→SVG wrapping when the editor markup carries no
 * explicit `width`/`height` (see `buildFallbackSvgFromHtmlBody`).
 */
const DEFAULT_FALLBACK_DIMENSION = 1080;

function parsePxDimension(
  style: string,
  prop: "width" | "height",
): number | null {
  const match = new RegExp(
    `(?:^|;)\\s*${prop}\\s*:\\s*(\\d+(?:\\.\\d+)?)px`,
    "i",
  ).exec(style);
  const captured = match?.[1];
  if (captured === undefined) return null;
  const value = Number(captured);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Some ad component `.svg` files were persisted as raw editor-HTML markup — a
 * `<div>`/`<img>` DOM with no `<svg>` root — instead of a real SVG (ADS-506).
 * Such a file can't be parsed as SVG: the layer blanks out in the renderer and
 * the editor fails to mount, both throwing "no <svg> element found".
 *
 * When we detect that shape (a non-empty `<body>` with no `<svg>`), wrap the
 * HTML in a minimal `<svg><foreignObject>` so the content still renders and the
 * ad opens for repair. Dimensions are inferred from the root element's inline
 * `width`/`height` (the editor markup stamps them), falling back to a square.
 * Returns null when there's nothing usable to wrap.
 */
export function buildFallbackSvgFromHtmlBody(body: HTMLElement): string | null {
  if (body.childElementCount === 0) return null;
  const inner = body.innerHTML.trim();
  if (!inner) return null;

  const rootStyle = body.firstElementChild?.getAttribute("style") ?? "";
  const width =
    parsePxDimension(rootStyle, "width") ?? DEFAULT_FALLBACK_DIMENSION;
  const height =
    parsePxDimension(rootStyle, "height") ?? DEFAULT_FALLBACK_DIMENSION;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" ` +
    `viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" ` +
    `data-editor-html-fallback="true">` +
    `<foreignObject x="0" y="0" width="${width}" height="${height}">` +
    `<div xmlns="http://www.w3.org/1999/xhtml" style="width:${width}px;height:${height}px">` +
    `${inner}</div></foreignObject></svg>`
  );
}
